import { useEffect, useState, useRef } from 'react';
import useRetrieveData from './useRetrieveData';
import { cleanCopy, guid, pluralize } from '../utils';
import { get, last, orderBy, size, unset, set, first, snakeCase, uniqBy, isObject } from '../lib/lodash';
import { useUser } from './useUser';
import { sendData } from '../../redux/actions/sendData';
import { getRowDefs } from '../../feature/report/ReportData/DataReports';
import { DateRanges, ReportDataStatus } from '../lib/filter';
import moment from 'moment';

export const TransformData = (raw) => {
  const { data, meta } = raw;
  const rels = get(meta, 'relationships', {});
  const hasError = get(meta, 'error');

  if (hasError) {
    return meta;
  }

  const findRel = (sel, id) => {
    return get(rels, sel, []).find((f) => f.id == id);
  };

  const isDateRow = (d) => {
    return ['year', 'month', 'quarter'].indexOf(d) > -1;
  };

  return data.map((d) => {
    const ids = Object.keys(d).filter((d) => {
      const isId = d.indexOf('_id') > -1;
      return isDateRow(d) || isId;
    });
    const relationships = {};
    ids.map((idkey) => {
      const [key] = idkey.split('_');
      if (isDateRow(idkey)) {
        const { month, year } = d;
        const dateKey = idkey;
        let _month = parseInt(month) - 1; //months start at 0 in moment
        _month = isNaN(_month) ? 1 : _month;
        const tme = dateKey === 'quarter' ? moment().month(_month).year(year).startOf('month').toISOString() : moment().month(_month).year(year).startOf(dateKey).toISOString();
        set(relationships, [dateKey, 'id'], tme);
      } else {
        set(relationships, key, findRel(pluralize(key), get(d, idkey)));
      }
    });
    return {
      ...d,
      relationships,
    };
  });
};

export const useChartSettings = props => {
  const { userBuilderRealm, siteContextRealmTier } = useUser();

  const loadRealmCharts = get(props, 'loadRealmCharts', true);

  const [state, setState] = useState({
    charts: [],
    chartsWithData: [],
    loadingChartData: false,
  })

  const loadKey = useRef();

  const realmcharts = useRetrieveData({ url: `/realm-charts` }, { 
    build: ['realmCharts'], 
    params: { 
      include: 'realms,userGroups',
      filter: { 'realm-access': props.isAdmin ? undefined : 1 }
    }, 
    version: 'v2',
    waitFor: loadRealmCharts ? undefined : null,
  });

  const { data } = realmcharts;

  useEffect(() => {
    if (data && size(data)) { 
      const charts = orderBy(data, 'config.order').map(c => {
        return {
          ...c,
          realms: size(c.realms) <= 1 ? c.realms : c.realms.filter(r => r.id != c.realmId),
        }
      });
      setState(s => ({ 
        ...s, 
        charts, 
        chartsWithData: charts.map(c => ({ ...formatChartForDisplay(c), loading: true })) 
      }))
    }
  }, [data]);

  const fixCamelCase = obj => {
    const newobj = {};
    for (let k in obj) {
      set(newobj, snakeCase(k), obj[k]);
    }
    return newobj;
  }

  const formatChartForDisplay = (chart, d, h, comp, comph) => {
    chart = cleanCopy(chart);
    set(chart, 'config.params.filter', fixCamelCase(get(chart, 'config.params.filter')));
    return {
      ...chart,
      ...get(chart, 'config', {}),
      ...get(chart, 'config.params.meta', {}),
      showSlider: false,
      data: !d ? [] : TransformData(d),
      headerRow: !h ? [] : TransformData(h),
      originalChartData: chart,
      compare: !comp ? null : TransformData(comp),
      compareHeader: !comph ? [] : TransformData(comph),
    }
  }

  const loadChartData = async (chart, filter, row) => {
    row = row || get(chart, 'config.params.meta.rows');
    let rawFilter = cleanCopy(filter);

    const getSavedChartParams = () => {
      const obj = get(chart, 'config.params.filter', {});
      const daterange = get(chart, 'config.daterange');

       if (daterange) {
        const dr = DateRanges.find(d => d.value == daterange);
        if (!!dr) {
          if (first(dr.vals)) { set(obj, 'start_date', first(dr.vals)) }
          if (last(dr.vals)) { set(obj, 'end_date', last(dr.vals)) }
        }
      }

      return obj;
    }

    const removeZeros = (obj) => {
      const newObj = {};
      for (let k in obj) {
        const val = obj[k];
        if (val !== 0 && val !== undefined) {
          newObj[k] = val;
        }
      }
      return newObj;
    }

    //this is because some filters set in the app are defaults and always get sent through
    //however we don't really want them to be
    const defaultIgnoreFilters = [{ key: 'status', value: 'active' }];
    defaultIgnoreFilters.map(f => {
      if (get(filter, f.key) == f.value) {
        unset(filter, f.key);
      }
    })
    
    const params = {
      ...get(chart, 'config.params', {}),
      filter: fixCamelCase({
        ...getSavedChartParams(),
        ...removeZeros(filter),
      })
    };

    const def = getRowDefs(row);

    if (row) {
      set(params, 'meta.rows', row);
    }

    const headerparams = cleanCopy(params);
    set(headerparams, 'meta.rows', get(def, 'parent', ''));

    const hasConfigCompare = get(chart, 'config.params.filter.showCompare');
    if (hasConfigCompare) {
      set(params, 'filter', fixCamelCase({ ...get(params, 'filter', {}), ...get(chart, 'config.params.filter') }));
      rawFilter = {
        ...rawFilter,
        ...get(chart, 'config.params.filter'),
        _showCompare: hasConfigCompare,
      }
      set(filter, 'compareType', rawFilter.compareType || filter.compareType);
    }

    let compareparams;
    let compareheaderparams;
    const showCompare = rawFilter._showCompare;
    // if (showCompare && rawFilter.status && rawFilter.compareStatus && rawFilter.status !== rawFilter.compareStatus) {
    if (showCompare) {
      compareparams = cleanCopy(params);
      set(compareparams, 'filter.status', rawFilter.compareStatus);
      
      compareheaderparams = cleanCopy(params);
      set(compareheaderparams, 'filter.status', rawFilter.compareStatus);
      set(compareheaderparams, 'meta.rows', get(def, 'parent', ''))

      set(chart, 'compareTitles', [ReportDataStatus.find(r => r.value == rawFilter.status), ReportDataStatus.find(r => r.value == rawFilter.compareStatus)]);
      set(chart, 'compareType', rawFilter.compareType);
    }

    set(chart, 'compareType', filter.compareType);

    const [res, header, compare, compareheader] = await Promise.all([
      sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params,
      }),
      sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params: headerparams,
      }),
      compareparams ?  sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params: compareparams,
      }) : null,
      compareheaderparams ?  sendData({ url: '/data-reports' }, {
        useNamespace: false,
        params: compareheaderparams,
      }) : null,
    ])
    
    const obj = formatChartForDisplay(chart, res.data, header.data, get(compare, 'data'), get(compareheader, 'data'));

    unset(obj, 'config');

    return obj;
  }

  const loadCharts = async (filter = {}, filteredCharts) => {
    const newLoadKey = guid();
    loadKey.current = newLoadKey;

    try {
      let validcharts = filteredCharts ? filteredCharts : state.charts;
      
      setState(s => ({ ...s, chartsWithData: validcharts.map(c => ({ ...formatChartForDisplay(c), loading: true })), loadingChartData: true }))
  
      const results = await Promise.all(validcharts.map(async chart => {
        return await loadChartData(chart, cleanCopy(filter));
      })) 

      if (loadKey.current == newLoadKey) {
        setState(s => ({ ...s, chartsWithData: results }))
      }
    } catch (err) {
      alert(err);
    } finally {
      if (loadKey.current == newLoadKey) {
        setState(s => ({ ...s, chartsWithData: s.chartsWithData.map(c => ({ ...c, loading: false })), loadingChartData: false }))
      }
    }
  }

  useEffect(() => {
    const shouldLoadCharts = get(props, 'loadCharts', true);

    if (size(state.charts) && shouldLoadCharts) {
      loadCharts();
    }
  }, [state.charts])

  const updateChart = async (nc) => {
    const id = get(nc, 'id');
    const method = id ? 'patch' : 'post';

    const attributes = { 
      realm_id: parseInt(userBuilderRealm.id),
      ...nc,
    };

    const removeAttributes = ['realmId', 'updatedAt', 'links'];
    removeAttributes.map(a => unset(attributes, a));

    const relationships = {};

    if (attributes.userGroups) {
      set(relationships, 'userGroups.data', attributes.userGroups.map(u => ({ id: `${get(u, 'id', u)}`, type: 'user-groups' })));
      unset(attributes, 'userGroups')
    }

    const attrealms = (() => {
      if (Array.isArray(attributes.realms)) {
        return attributes.realms;
      } else if (isObject(attributes.realms)) {
        return Object.keys(attributes.realms).map(k => attributes.realms[k]);
      }
    })();

    const realms = uniqBy([...attrealms.map(k => ({ id: k.id, type: 'realms' }))], 'id');
    if (realms) {
      set(relationships, 'realms.data', realms);
      unset(attributes, 'realms')
    }

    unset(attributes, 'id');

    if (attributes.order != undefined) {
      set(attributes, 'config.order', attributes.order);
      unset(attributes, 'order');
    }

    unset(attributes, 'realmTier');
    unset(attributes, 'isGlobal');

    return await sendData({
      url: id ? `/realm-charts/${id}` : `/realm-charts`,
      method,
      data: {
        data: {
          id,
          type: 'realm-charts',
          attributes,
          relationships,
        }
      },
    }, { version: 'v2' });
  }

  const updateCharts = async charts => {
    return await Promise.all(charts.map(updateChart));
  }

  const deleteChart = async (chart) => {
    const id = `${chart.id}`;
    await sendData({ 
      url: `/realm-charts/${id}`, 
      method: 'patch',
      data: {
        data: {
          id,
          type: 'realm-charts',
          attributes: {
            archived: true
          }
        }
      }
    }, { version: 'v2' })

    await realmcharts.update()
  }

  const createAndSave = async vals => {
    try {  
      const chart = vals.chart && vals.chart.id ? vals.chart : {
        name: get(vals, 'name', ''),
        description: get(vals, 'description', ''),
        userGroups: get(vals, 'userGroups', []),
        realms: get(vals, 'realms', []),
        realm_tier: siteContextRealmTier,
        config: {
          order: state.charts.length,
          daterange: get(vals, 'chartProps.chartGlobalFilters.daterange'),
          chartIndex: get(vals, 'chartIndex'),
          showCounts: get(vals, 'chartProps.showCounts'),
          useChart: get(vals, 'chartProps.useChart'),
          slide: get(vals, 'chartProps.slide'),
          params: {
            ...get(vals, 'chartProps.opts.params', get(vals, 'chartProps.params', {})),
            filter: {
              ...get(vals, 'chartProps.opts.params.filter', get(vals, 'chartProps.params.filter', {})),
              realm_id: undefined,
            }
          },
        }
      }
  
      await updateChart(chart);
      
      await realmcharts.update()
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  const filterChartsByUserGroups = (groups, charts) => {
    const flatgroups = groups.map(m => m.id || m);
    return charts.filter(c => {
      const { userGroups } = c;
      const ugs = userGroups.map(ug => ug.id);
      return !!ugs.find(g => flatgroups.includes(g));
    })
  }

  const filterChartsByRealmTier = (realm_tier, charts) => {
    if (!realm_tier) { return charts };

    return charts.filter(c => {
      return get(c, 'realmTier') == realm_tier;
    })
  }

  return {
    ...realmcharts,
    data,
    ...state,
    createAndSave,
    filterChartsByUserGroups,
    deleteChart,
    updateChart,
    updateCharts,
    filterChartsByRealmTier,
    loadCharts,
    loadChartData,
  }
}